import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Rating,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from '@mui/material';
import {
  propertyApi,
  placesApi,
  type PropertyData,
  GewType,
} from '../services';
import { useToast } from '../contexts/ToastContext';
import PropertyEvaluationChart from '../components/PropertyEvaluationChart';
import PropertyFiles from '../components/PropertyFiles';
import './PropertyPage.css';

const EditPropertyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const emptyProperty: PropertyData = {
    id: '',
    address: '',
    country: 'Österreich',
    gain: 0,
    gainPercentage: 0,
    features: {},
    ratings: {},
  };

  const [property, setProperty] = useState<PropertyData>(emptyProperty);
  const [originalProperty, setOriginalProperty] =
    useState<PropertyData>(emptyProperty);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<number>(-1);
  // Separate suggestions for city field
  const [citySuggestions, setCitySuggestions] = useState<any[]>([]);
  const [isSearchingCity, setIsSearchingCity] = useState(false);
  const [selectedCityIndex, setSelectedCityIndex] = useState<number>(-1);
  const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  // Refs for map
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerInstanceRef = useRef<google.maps.Marker | null>(null);

  // Ref for debounce timeout
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceCityRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref for menu anchor
  const menuAnchorRef = useRef<HTMLButtonElement>(null);

  // Function to update map location
  const updateMapLocation = (lat: number, lng: number) => {
    if (mapInstanceRef.current) {
      const position = { lat, lng };
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(15);

      const googleMaps = (window as any).google;
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setPosition(position);
      } else if (googleMaps && googleMaps.maps) {
        markerInstanceRef.current = new googleMaps.maps.Marker({
          position,
          map: mapInstanceRef.current,
        });
      }
    }
  };

  // Function to geocode an address using Google Maps Geocoder
  const geocodeAddress = (
    address: string,
  ): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      const googleMaps = (window as any).google;
      if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Geocoder) {
        resolve(null);
        return;
      }

      const geocoder = new googleMaps.maps.Geocoder();
      geocoder.geocode(
        { address, region: 'at' },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus,
        ) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
            });
          } else {
            resolve(null);
          }
        },
      );
    });
  };

  // Function to initialize map (can be called multiple times safely)
  const initializeMap = () => {
    if (!mapRef.current) {
      return false;
    }

    // Check if element is actually in the DOM
    if (!document.contains(mapRef.current)) {
      return false;
    }

    // Ensure the container has dimensions - Google Maps requires explicit dimensions
    const rect = mapRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Map) {
      return false;
    }

    if (!mapInstanceRef.current) {
      try {
        // Check if Google Maps already initialized this container
        const hasGoogleMapsContent =
          mapRef.current.querySelector('[class*="gm-"]') ||
          mapRef.current.querySelector('div[style*="overflow"]');

        if (!hasGoogleMapsContent) {
          // Clear only if no Google Maps content exists
          mapRef.current.innerHTML = '';
        }

        mapInstanceRef.current = new googleMaps.maps.Map(mapRef.current, {
          center: { lat: 47.5162, lng: 14.5501 }, // Default to Austria
          zoom: 7,
          disableDefaultUI: true,
        });

        // Trigger resize after a short delay to ensure map renders correctly
        setTimeout(() => {
          if (mapInstanceRef.current && mapRef.current) {
            const googleMaps = (window as any).google;
            if (googleMaps && googleMaps.maps && googleMaps.maps.event) {
              googleMaps.maps.event.trigger(mapInstanceRef.current, 'resize');

              // Re-initialize if container was cleared after resize
              if (mapRef.current.children.length === 0) {
                mapInstanceRef.current = null;
                setIsMapInitialized(false);
                initializeMap();
              }
            }
          }
        }, 200);

        setIsMapInitialized(true);
        return true;
      } catch (error) {
        return false;
      }
    }

    return true;
  };

  // Initialize map - retry until Google Maps is loaded
  // Also re-initialize when property ID changes (navigate to different property)
  useEffect(() => {
    // Store the current ID to prevent race conditions
    const currentId = id;
    let isMounted = true;

    // Reset map instance and state when property ID changes
    mapInstanceRef.current = null;
    markerInstanceRef.current = null;
    setIsMapInitialized(false);

    // Small delay to ensure DOM is ready and avoid React StrictMode double-initialization issues
    const initTimeout = setTimeout(() => {
      if (!isMounted || currentId !== id) return;

      // Try to initialize immediately
      if (initializeMap()) {
        return; // Map initialized successfully
      }

      // If not initialized, set up a retry mechanism
      let retryCount = 0;
      const maxRetries = 100; // 10 seconds max wait time (100 * 100ms)

      const retryInterval = setInterval(() => {
        if (!isMounted || currentId !== id) {
          clearInterval(retryInterval);
          return;
        }

        retryCount++;
        if (initializeMap()) {
          clearInterval(retryInterval);
        } else if (retryCount >= maxRetries) {
          clearInterval(retryInterval);
        }
      }, 100);

      return () => clearInterval(retryInterval);
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      markerInstanceRef.current = null;
    };
  }, [id]); // Re-run when property ID changes

  // Update map location when coordinates are available (e.g., after save or when property loads)
  useEffect(() => {
    if (
      property.latitude !== undefined &&
      property.longitude !== undefined &&
      property.latitude !== null &&
      property.longitude !== null
    ) {
      // Wait for map to be initialized before updating
      if (isMapInitialized && mapInstanceRef.current) {
        // Map is ready, update location immediately
        updateMapLocation(property.latitude, property.longitude);
      } else {
        // Map not ready yet, wait a bit and try again
        let retryCount = 0;
        const maxRetries = 100;

        const tryUpdate = () => {
          if (isMapInitialized && mapInstanceRef.current) {
            updateMapLocation(property.latitude!, property.longitude!);
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(tryUpdate, 100);
          }
        };

        setTimeout(tryUpdate, 100);
      }
    }
  }, [property.latitude, property.longitude, isMapInitialized]);

  // Helper function to convert property from API format to display format
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    // Remove any fields that shouldn't be in PropertyData (like userId, user)
    const { userId: _, user: __, ...cleanProp } = prop as any;

    return {
      ...cleanProp,
      kaufdatum: cleanProp.kaufdatum
        ? typeof cleanProp.kaufdatum === 'string'
          ? cleanProp.kaufdatum.split('T')[0]
          : new Date(cleanProp.kaufdatum).toISOString().split('T')[0]
        : undefined,
      // Convert string numbers from backend to actual numbers
      kaufpreis:
        cleanProp.kaufpreis !== undefined && cleanProp.kaufpreis !== null
          ? typeof cleanProp.kaufpreis === 'string'
            ? parseFloat(cleanProp.kaufpreis)
            : cleanProp.kaufpreis
          : undefined,
      currentValue:
        cleanProp.currentValue !== undefined && cleanProp.currentValue !== null
          ? typeof cleanProp.currentValue === 'string'
            ? parseFloat(cleanProp.currentValue)
            : cleanProp.currentValue
          : undefined,
      gain:
        typeof cleanProp.gain === 'string'
          ? parseFloat(cleanProp.gain)
          : cleanProp.gain,
      gainPercentage:
        typeof cleanProp.gainPercentage === 'string'
          ? parseFloat(cleanProp.gainPercentage)
          : cleanProp.gainPercentage,
      // Convert latitude/longitude from string to number if needed
      latitude:
        cleanProp.latitude !== undefined && cleanProp.latitude !== null
          ? typeof cleanProp.latitude === 'string'
            ? parseFloat(cleanProp.latitude)
            : cleanProp.latitude
          : undefined,
      longitude:
        cleanProp.longitude !== undefined && cleanProp.longitude !== null
          ? typeof cleanProp.longitude === 'string'
            ? parseFloat(cleanProp.longitude)
            : cleanProp.longitude
          : undefined,
      // Always set country to Österreich if not set
      country: cleanProp.country || 'Österreich',
      features: cleanProp.features || {},
      ratings: cleanProp.ratings || {},
    };
  };

  useEffect(() => {
    const fetchProperty = async () => {
      if (id) {
        setLoading(true);
        try {
          const propertyData = await propertyApi.getById(id);
          const normalized = normalizeProperty(propertyData);
          setProperty(normalized);
          setOriginalProperty(normalized);
          setHasUnsavedChanges(false);

          // If coordinates don't exist but address does, geocode it (fallback for old properties)
          // The useEffect watching coordinates will handle updating the map when coordinates are available
          if (
            !normalized.latitude &&
            !normalized.longitude &&
            normalized.address
          ) {
            geocodeAddress(normalized.address)
              .then((location) => {
                if (location) {
                  // Update property with geocoded coordinates (user can save to persist)
                  setProperty((prev) => ({
                    ...prev,
                    latitude: location.lat,
                    longitude: location.lng,
                  }));
                }
              })
              .catch(() => {
                // Silently fail - coordinates will remain undefined
              });
          }
        } catch (error: any) {
          showError(error.message || 'Failed to load property');
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProperty();
  }, [id]);

  // Check if property has changed compared to original
  useEffect(() => {
    const {
      id: _id,
      gain: _gain,
      gainPercentage: _gainPercentage,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      ...compareProp
    } = property as any;
    const {
      id: _origId,
      gain: _origGain,
      gainPercentage: _origGainPercentage,
      createdAt: _origCreatedAt,
      updatedAt: _origUpdatedAt,
      ...origProp
    } = originalProperty as any;

    const hasChanges = JSON.stringify(compareProp) !== JSON.stringify(origProp);
    setHasUnsavedChanges(hasChanges);
  }, [property, originalProperty]);

  const handleFieldClick = (
    fieldName: string,
    currentValue: string | number | boolean,
  ) => {
    setEditingField(fieldName);
    setTempValue(String(currentValue ?? ''));
  };

  // Handle address input change with debouncing for autocomplete (for streetName)
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempValue(value);
    setProperty((prev) => ({ ...prev, streetName: value }));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!value) {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await placesApi.getAutocomplete(value);
        setSuggestions(result);
        setSelectedSuggestionIndex(-1); // Reset selection when new suggestions arrive
      } catch (error) {
        console.error(error);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce delay
  };

  // Handle city input change with debouncing for autocomplete
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempValue(value);
    setProperty((prev) => ({ ...prev, city: value }));

    if (debounceCityRef.current) {
      clearTimeout(debounceCityRef.current);
    }

    if (!value) {
      setCitySuggestions([]);
      setSelectedCityIndex(-1);
      return;
    }

    debounceCityRef.current = setTimeout(async () => {
      setIsSearchingCity(true);
      try {
        const result = await placesApi.getCities(value);
        setCitySuggestions(result);
        setSelectedCityIndex(-1);
      } catch (error) {
        console.error(error);
        setCitySuggestions([]);
        setSelectedCityIndex(-1);
      } finally {
        setIsSearchingCity(false);
      }
    }, 300);
  };

  // Helper function to find best match from DB for parsed city
  const findBestCityMatch = (
    parsed: { city?: string; sublocality?: string; postalCode?: string },
    dbCities: string[],
  ): string | null => {
    if (!parsed.city || dbCities.length === 0) return null;

    const cityLower = parsed.city.toLowerCase();

    // 1. Check for an exact match (case-insensitive)
    const exactMatch = dbCities.find((c) => c.toLowerCase() === cityLower);
    if (exactMatch) return exactMatch;

    // 2. If there are sublocalities/neighborhoods, try to match them
    if (parsed.sublocality) {
      const subLower = parsed.sublocality.toLowerCase();
      const subMatch = dbCities.find((c) => {
        const cLower = c.toLowerCase();
        return cLower.includes(cityLower) && cLower.includes(subLower);
      });
      if (subMatch) return subMatch;
    }

    // 3. For Vienna, match using postal code
    if (parsed.postalCode && (cityLower === 'wien' || cityLower === 'vienna')) {
      const zip = parsed.postalCode;
      if (zip.startsWith('1') && zip.length === 4) {
        const districtNum = parseInt(zip.substring(1, 3), 10);
        if (districtNum >= 1 && districtNum <= 23) {
          const pattern = `wien ${districtNum}.`;
          const districtMatch = dbCities.find((c) =>
            c.toLowerCase().includes(pattern),
          );
          if (districtMatch) return districtMatch;
        }
      }
    }

    // 4. If single match starting with city name
    if (
      dbCities.length === 1 &&
      dbCities[0].toLowerCase().startsWith(cityLower)
    ) {
      return dbCities[0];
    }

    // 5. Fallback to first matching prefix
    const prefixMatch = dbCities.find((c) =>
      c.toLowerCase().startsWith(cityLower),
    );
    if (prefixMatch) return prefixMatch;

    return null;
  };

  // Helper function to parse address components from Places API response
  const parseAddressComponents = (
    addressComponents: any[],
  ): {
    streetName?: string;
    houseNumber?: string;
    city?: string;
    country?: string;
    sublocality?: string;
    postalCode?: string;
    administrativeArea3?: string;
    administrativeArea2?: string;
  } => {
    const result: {
      streetName?: string;
      houseNumber?: string;
      city?: string;
      country?: string;
      sublocality?: string;
      postalCode?: string;
      administrativeArea3?: string;
      administrativeArea2?: string;
    } = {};

    if (!addressComponents || !Array.isArray(addressComponents)) {
      console.log(
        'No address components found or not an array:',
        addressComponents,
      );
      return result;
    }

    console.log('Parsing address components:', addressComponents);

    for (const component of addressComponents) {
      const types = component.types || [];
      const longText = component.longText;
      const shortText = component.shortText;
      const text = component.text;
      const value = longText || shortText || text;

      console.log('Component:', { types, longText, shortText, text, value });

      if (types.includes('street_number')) {
        result.houseNumber = value;
        console.log('Found house number:', result.houseNumber);
      } else if (types.includes('route')) {
        result.streetName = value;
        console.log('Found street name:', result.streetName);
      } else if (types.includes('locality') || types.includes('postal_town')) {
        result.city = value;
        console.log('Found city:', result.city);
      } else if (types.includes('country')) {
        result.country = value;
        console.log('Found country:', result.country);
      } else if (
        types.includes('sublocality') ||
        types.includes('sublocality_level_1') ||
        types.includes('neighborhood')
      ) {
        result.sublocality = value;
        console.log('Found sublocality:', result.sublocality);
      } else if (types.includes('postal_code')) {
        result.postalCode = value;
        console.log('Found postal code:', result.postalCode);
      } else if (types.includes('administrative_area_level_3')) {
        result.administrativeArea3 = value;
        console.log('Found admin area 3:', result.administrativeArea3);
      } else if (types.includes('administrative_area_level_2')) {
        result.administrativeArea2 = value;
        console.log('Found admin area 2:', result.administrativeArea2);
      }
    }

    console.log('Parsed result:', result);
    return result;
  };

  // Handle suggestion selection
  const handleSuggestionClick = async (suggestion: any, fieldName?: string) => {
    const placeId = suggestion.placeId || suggestion.placePrediction?.placeId;
    const address =
      suggestion.text?.text ||
      suggestion.placePrediction?.text?.text ||
      suggestion.text ||
      suggestion.placePrediction?.structuredFormat?.mainText?.text ||
      'Unknown address';

    console.log('Suggestion clicked:', {
      placeId,
      address,
      fieldName,
      suggestion,
    });

    // Close suggestions for all fields
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setCitySuggestions([]);
    setSelectedCityIndex(-1);

    // Close any open editing field
    setEditingField(null);
    setTempValue('');

    if (placeId) {
      try {
        const details = await placesApi.getPlaceDetails(placeId);
        console.log('Place details received:', details);

        if (details?.location) {
          const lat = details.location.latitude;
          const lng = details.location.longitude;

          // Parse address components
          const addressComponents = details.addressComponents || [];
          const parsed = parseAddressComponents(addressComponents);

          // Build full address from components or use formatted address
          const fullAddress = details.formattedAddress || address;

          let matchedCity = '';
          try {
            const cityFromCoordinates = await placesApi.getCityByCoordinates(
              lat,
              lng,
            );
            if (cityFromCoordinates) {
              matchedCity = cityFromCoordinates;
            }
          } catch (err) {
            console.error('Error fetching city by coordinates:', err);
          }

          // Fallback safeguard: if GeoJSON lookup returned null, try the old resolution logic
          if (!matchedCity) {
            const cityCandidates = [
              parsed.city,
              parsed.administrativeArea3,
              parsed.administrativeArea2,
              parsed.sublocality,
            ].filter(Boolean) as string[];

            for (const candidate of cityCandidates) {
              try {
                const dbCities = await placesApi.getCities(candidate);
                const tempParsed = { ...parsed, city: candidate };
                const bestMatch = findBestCityMatch(tempParsed, dbCities);
                if (bestMatch) {
                  matchedCity = bestMatch;
                  break; // Found a valid database match!
                }
              } catch (err) {
                console.error(
                  'Error validating city candidate against database:',
                  err,
                );
              }
            }
          }

          console.log('Updating property with:', {
            address: fullAddress,
            streetName: parsed.streetName,
            houseNumber: parsed.houseNumber,
            city: matchedCity || '',
            country: 'Österreich',
            lat,
            lng,
          });

          // Update property with address components and coordinates
          // Use parsed values if they exist, otherwise keep previous values
          setProperty((prev) => {
            const updated = {
              ...prev,
              address: fullAddress,
              country: 'Österreich', // Always set to Österreich
              latitude: lat,
              longitude: lng,
            };

            // Only update fields if parsed values exist (not undefined)
            if (parsed.streetName !== undefined) {
              updated.streetName = parsed.streetName;
            }
            if (parsed.houseNumber !== undefined) {
              updated.houseNumber = parsed.houseNumber;
            }

            if (matchedCity) {
              updated.city = matchedCity;
            } else {
              updated.city = '';
            }

            return updated;
          });

          // Update map location
          updateMapLocation(lat, lng);
        } else {
          console.error('No location found in details:', details);
          showError('Could not find location for this address.');
          // Still update address even if location fails
          setProperty((prev) => ({ ...prev, address }));
        }
      } catch (error) {
        console.error('Error fetching place details:', error);
        showError('Failed to get address details.');
        // Still update address even if details fetch fails
        setProperty((prev) => ({ ...prev, address }));
      }
    } else {
      console.warn('No placeId found in suggestion');
      // No placeId, just update address
      setProperty((prev) => ({ ...prev, address }));
    }
  };

  // Handle city suggestion selection
  const handleCitySuggestionClick = (city: string) => {
    setCitySuggestions([]);
    setSelectedCityIndex(-1);
    setEditingField(null);
    setTempValue('');

    let updatedAddress = '';
    setProperty((prev) => {
      const updated = {
        ...prev,
        city,
        country: 'Österreich',
      };

      const parts = [];
      if (updated.streetName) parts.push(updated.streetName);
      if (updated.houseNumber) parts.push(updated.houseNumber);
      if (updated.city) parts.push(updated.city);
      if (updated.country) parts.push(updated.country);
      updated.address = parts.join(', ');
      updatedAddress = updated.address;

      return updated;
    });

    // Since address/city changed, geocode the new address to update the map coordinates
    setTimeout(() => {
      const newAddress =
        updatedAddress ||
        `${property.streetName || ''} ${property.houseNumber || ''}, ${city}, Österreich`.trim();
      geocodeAddress(newAddress)
        .then((location) => {
          if (location) {
            setProperty((prev) => ({
              ...prev,
              latitude: location.lat,
              longitude: location.lng,
            }));
            updateMapLocation(location.lat, location.lng);
          }
        })
        .catch((err) => {
          console.error('Geocoding error:', err);
        });
    }, 0);
  };

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (debounceCityRef.current) {
        clearTimeout(debounceCityRef.current);
      }
    };
  }, []);

  const handleFieldSave = (fieldName: string) => {
    const numericFields = ['kaufpreis', 'currentValue', 'gain', 'area'];
    const booleanFields = [
      'features.elevator',
      'features.balcony',
      'features.garage',
      'features.basement',
      'features.accessibility',
      'features.silentEnvironment',
      'features.publicTransport',
      'features.nearSupermarket',
      'features.thermallyRenovated',
      'features.modernHeating',
    ];

    let value: any;
    if (booleanFields.includes(fieldName)) {
      value = tempValue === 'true' || tempValue === '1';
    } else if (numericFields.includes(fieldName)) {
      value = tempValue === '' ? undefined : parseFloat(tempValue);
    } else {
      value = tempValue === '' ? undefined : tempValue;
    }

    setProperty((prev) => {
      const updated: any = { ...prev };

      if (fieldName.startsWith('features.')) {
        updated.features = {
          ...prev.features,
          [fieldName.split('.')[1]]: value,
        };
      } else {
        updated[fieldName] = value;
      }

      // Always set country to Österreich
      if (fieldName === 'country' || !prev.country) {
        updated.country = 'Österreich';
      }

      // Update full address when address components change
      if (
        fieldName === 'streetName' ||
        fieldName === 'houseNumber' ||
        fieldName === 'city'
      ) {
        const parts = [];
        if (updated.streetName) parts.push(updated.streetName);
        if (updated.houseNumber) parts.push(updated.houseNumber);
        if (updated.city) parts.push(updated.city);
        if (updated.country) parts.push(updated.country);
        updated.address = parts.join(', ') || prev.address || '';
      }

      return updated;
    });

    // Recalculate gain if purchase price or current value changed
    if (fieldName === 'kaufpreis' || fieldName === 'currentValue') {
      const newKaufpreis =
        fieldName === 'kaufpreis' ? (value as number) : property.kaufpreis || 0;
      const newCurrentValue =
        fieldName === 'currentValue'
          ? (value as number)
          : property.currentValue || 0;
      const newGain = newCurrentValue - newKaufpreis;
      const newGainPercentage =
        newKaufpreis > 0 ? (newGain / newKaufpreis) * 100 : 0;

      setProperty((prev) => ({
        ...prev,
        gain: newGain,
        gainPercentage: newGainPercentage,
      }));
    }

    setEditingField(null);
    setTempValue('');
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleFieldKeyDown = (e: React.KeyboardEvent, fieldName: string) => {
    if (e.key === 'Enter') {
      handleFieldSave(fieldName);
    } else if (e.key === 'Escape') {
      handleFieldCancel();
    }
  };

  const handleMenuToggle = () => {
    setMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMenuClose = (event: Event | React.SyntheticEvent) => {
    if (
      menuAnchorRef.current &&
      menuAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpen(false);
  };

  function handleMenuListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setMenuOpen(false);
    } else if (event.key === 'Escape') {
      setMenuOpen(false);
    }
  }

  const prevMenuOpen = useRef(menuOpen);
  useEffect(() => {
    if (prevMenuOpen.current === true && menuOpen === false) {
      menuAnchorRef.current?.focus();
    }
    prevMenuOpen.current = menuOpen;
  }, [menuOpen]);

  const handleDelete = async () => {
    setMenuOpen(false);
    if (!id || id === 'new') return;

    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await propertyApi.delete(id);
        showSuccess('Property deleted successfully');
        navigate('/dashboard');
      } catch (error: any) {
        showError(error.message || 'Failed to delete property');
      }
    }
  };

  const handleSave = async () => {
    if (!hasUnsavedChanges || !id) return;

    setSaving(true);
    try {
      // Exclude fields that shouldn't be sent in update
      const {
        id: _,
        gain,
        gainPercentage,
        createdAt,
        updatedAt,
        userId: __,
        user: ___,
        ...updateData
      } = property as any;

      const updatedProperty = await propertyApi.update(id, updateData);
      const normalized = normalizeProperty(updatedProperty);
      setProperty(normalized);
      setOriginalProperty(normalized);
      setHasUnsavedChanges(false);
      showSuccess('Property updated successfully');
      navigate(`/properties/${id}`);
    } catch (error: any) {
      showError(error.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const renderEditableField = (
    fieldName: string,
    label: string,
    value: any,
    type:
      | 'text'
      | 'number'
      | 'date'
      | 'select'
      | 'textarea'
      | 'checkbox' = 'text',
    options?: string[],
  ) => {
    const isEditing = editingField === fieldName;
    const displayValue =
      value !== undefined && value !== null ? String(value) : '';

    if (type === 'checkbox') {
      const rawValue =
        value && typeof value === 'object' && 'value' in value
          ? value.value
          : value;
      const boolValue =
        rawValue === true || rawValue === 'true' || rawValue === 1;
      return (
        <ToggleButtonGroup
          value={String(boolValue)}
          exclusive
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              const bValue = newValue === 'true';
              setProperty((prev) => {
                if (fieldName.startsWith('features.')) {
                  const key = fieldName.split('.')[1];
                  const currentObj = prev.features?.[key];
                  const localization =
                    currentObj &&
                    typeof currentObj === 'object' &&
                    'localization' in currentObj
                      ? currentObj.localization
                      : label;
                  return {
                    ...prev,
                    features: {
                      ...prev.features,
                      [key]: { value: bValue, localization },
                    },
                  };
                }
                return { ...prev, [fieldName]: bValue };
              });
            }
          }}
          aria-label={label}
        >
          <ToggleButton value="true" aria-label="Yes">
            Yes
          </ToggleButton>
          <ToggleButton value="false" aria-label="No">
            No
          </ToggleButton>
        </ToggleButtonGroup>
      );
    }

    if (isEditing) {
      if (type === 'select' && options) {
        // Ensure tempValue matches one of the options, or use the current value
        const selectValue = tempValue || displayValue || '';
        return (
          <select
            value={selectValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={() => handleFieldSave(fieldName)}
            onKeyDown={(e) => handleFieldKeyDown(e, fieldName)}
            autoFocus
            className="inline-edit-input"
          >
            <option value="">-- Select --</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      } else if (type === 'textarea') {
        return (
          <textarea
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={() => handleFieldSave(fieldName)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleFieldCancel();
              }
            }}
            autoFocus
            className="inline-edit-input inline-edit-textarea"
            rows={4}
          />
        );
      } else {
        // Special handling for address fields with autocomplete suggestions (excluding houseNumber)
        if (fieldName === 'streetName' || fieldName === 'city') {
          const currentSuggestions =
            fieldName === 'city' ? citySuggestions : suggestions;
          const currentIsSearching =
            fieldName === 'city' ? isSearchingCity : isSearching;
          const currentSelectedIndex =
            fieldName === 'city' ? selectedCityIndex : selectedSuggestionIndex;
          const setCurrentSelectedIndex =
            fieldName === 'city'
              ? setSelectedCityIndex
              : setSelectedSuggestionIndex;
          const setCurrentSuggestions =
            fieldName === 'city' ? setCitySuggestions : setSuggestions;
          const handleChange =
            fieldName === 'city' ? handleCityChange : handleAddressChange;

          // Create keyboard handler for this field
          const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (currentSuggestions.length === 0) {
              handleFieldKeyDown(e, fieldName);
              return;
            }

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setCurrentSelectedIndex((prev) =>
                prev < currentSuggestions.length - 1 ? prev + 1 : prev,
              );
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setCurrentSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (
                currentSelectedIndex >= 0 &&
                currentSelectedIndex < currentSuggestions.length
              ) {
                if (fieldName === 'city') {
                  handleCitySuggestionClick(
                    currentSuggestions[currentSelectedIndex],
                  );
                } else {
                  handleSuggestionClick(
                    currentSuggestions[currentSelectedIndex],
                    fieldName,
                  );
                }
              } else {
                handleFieldSave(fieldName);
              }
            } else if (e.key === 'Escape') {
              setCurrentSuggestions([]);
              setCurrentSelectedIndex(-1);
              handleFieldCancel();
            }
          };

          return (
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={type}
                value={tempValue}
                onChange={handleChange}
                onBlur={() => {
                  // Delay hiding suggestions to allow click on suggestion
                  setTimeout(() => {
                    setCurrentSuggestions([]);
                    setCurrentSelectedIndex(-1);
                    handleFieldSave(fieldName);
                  }, 200);
                }}
                onKeyDown={handleKeyDown}
                autoFocus
                className="inline-edit-input"
                placeholder={
                  fieldName === 'streetName'
                    ? 'Start typing a street name...'
                    : 'Start typing a city...'
                }
                step={type === 'number' ? 'any' : undefined}
              />
              {currentIsSearching && (
                <div
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.875rem',
                    color: '#666',
                  }}
                >
                  Searching...
                </div>
              )}
              {currentSuggestions.length > 0 && (
                <ul
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    border: '1px solid #ccc',
                    backgroundColor: 'white',
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  {currentSuggestions.map((suggestion, index) => {
                    if (fieldName === 'city') {
                      const isSelected = index === currentSelectedIndex;
                      return (
                        <li
                          key={index}
                          onClick={() => handleCitySuggestionClick(suggestion)}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setCurrentSelectedIndex(index)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                            backgroundColor: isSelected ? '#e7f3ff' : 'white',
                          }}
                        >
                          <div style={{ fontWeight: 500, color: '#333' }}>
                            {suggestion}
                          </div>
                        </li>
                      );
                    }

                    // Handle both direct structure and placePrediction wrapper
                    const displayText =
                      suggestion.text?.text ||
                      suggestion.placePrediction?.text?.text ||
                      suggestion.text ||
                      suggestion.placePrediction?.structuredFormat?.mainText
                        ?.text ||
                      'Unknown';
                    const secondaryText =
                      suggestion.placePrediction?.structuredFormat
                        ?.secondaryText?.text;
                    const isSelected = index === currentSelectedIndex;

                    return (
                      <li
                        key={index}
                        onClick={() =>
                          handleSuggestionClick(suggestion, fieldName)
                        }
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
                        onMouseEnter={() => setCurrentSelectedIndex(index)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          backgroundColor: isSelected ? '#e7f3ff' : 'white',
                        }}
                      >
                        <div style={{ fontWeight: 500, color: '#333' }}>
                          {displayText}
                        </div>
                        {secondaryText && (
                          <div
                            style={{
                              fontSize: '0.875rem',
                              color: '#555',
                              marginTop: '2px',
                            }}
                          >
                            {secondaryText}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        }

        // Country field - always set to Österreich and read-only
        if (fieldName === 'country') {
          return (
            <input
              type="text"
              value="Österreich"
              readOnly
              className="inline-edit-input"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          );
        }
        return (
          <input
            type={type}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={() => handleFieldSave(fieldName)}
            onKeyDown={(e) => handleFieldKeyDown(e, fieldName)}
            autoFocus
            className="inline-edit-input"
            step={type === 'number' ? 'any' : undefined}
          />
        );
      }
    }

    return (
      <div
        className="inline-edit-display"
        onClick={() => handleFieldClick(fieldName, value || '')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFieldClick(fieldName, value || '');
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Edit ${label}`}
        title="Click to edit or press Space or Enter"
      >
        {displayValue ||
          (fieldName === 'houseNumber' ? (
            ''
          ) : (
            <span className="placeholder">
              Click to add {label.toLowerCase()}
            </span>
          ))}
      </div>
    );
  };

  const renderRatingField = (fieldName: string) => {
    const key = fieldName.split('.')[1];
    const valObj = property.ratings?.[key];
    const val =
      valObj && typeof valObj === 'object' && 'value' in valObj
        ? valObj.value
        : Number(valObj) || 1;
    const localization =
      valObj && typeof valObj === 'object' && 'localization' in valObj
        ? valObj.localization
        : key;
    return (
      <div
        className="rating-field-container"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <Rating
          name={fieldName}
          value={val}
          precision={0.5}
          size="medium"
          sx={{
            color: '#faaf00',
            fontSize: '1.5rem',
            '& .MuiRating-iconFilled': { color: '#faaf00' },
            '& .MuiRating-iconHover': { color: '#faaf00' },
          }}
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              setProperty((prev) => ({
                ...prev,
                ratings: {
                  ...prev.ratings,
                  [key]: {
                    value: newValue || 1,
                    localization,
                  },
                },
              }));
            }
          }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="property-page">
        <div className="property-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Loading property...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="property-page">
      <div className="property-container">
        <header className="property-header">
          <button
            className="back-button"
            onClick={() => {
              navigate(`/properties/${id}`);
            }}
          >
            ← Back to Property
          </button>
          <div className="header-actions">
            <div>
              <Button
                ref={menuAnchorRef}
                id="property-menu-button"
                aria-controls={menuOpen ? 'property-menu' : undefined}
                aria-expanded={menuOpen ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleMenuToggle}
                className="menu-dots-button"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="5" r="2" fill="currentColor" />
                  <circle cx="12" cy="12" r="2" fill="currentColor" />
                  <circle cx="12" cy="19" r="2" fill="currentColor" />
                </svg>
              </Button>
              <Popper
                open={menuOpen}
                anchorEl={menuAnchorRef.current}
                role={undefined}
                placement="bottom-end"
                transition
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom-end'
                          ? 'right top'
                          : 'right bottom',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleMenuClose}>
                        <MenuList
                          autoFocusItem={menuOpen}
                          id="property-menu"
                          aria-labelledby="property-menu-button"
                          onKeyDown={handleMenuListKeyDown}
                        >
                          <MenuItem onClick={handleDelete}>Delete</MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </div>
          </div>
        </header>

        <div className="property-content">
          <h1 className="property-title">
            {[
              [property.streetName, property.houseNumber]
                .filter(Boolean)
                .join(' '),
              property.city,
              property.country,
            ]
              .filter(Boolean)
              .join(' • ') ||
              property.address ||
              'Property Details'}
          </h1>

          <div className="property-layout">
            {/* Left Column */}
            <div className="property-left-column">
              {/* Map Section */}
              <div className="map-section">
                <div
                  ref={mapRef}
                  className="map-container"
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                  }}
                />
              </div>

              {/* General Data Section */}
              <div className="detail-section">
                <h2>General Data</h2>
                <div className="detail-list">
                  {/* Address Fields */}
                  <div className="detail-item-row">
                    <div
                      className="detail-item"
                      style={{
                        position: 'relative',
                        width: '80%',
                        flex: '1 1 80%',
                      }}
                    >
                      <label>Street Name</label>
                      {renderEditableField(
                        'streetName',
                        'Street Name',
                        property.streetName,
                        'text',
                      )}
                    </div>
                    <div
                      className="detail-item"
                      style={{
                        position: 'relative',
                        width: '20%',
                        flex: '0 0 20%',
                      }}
                    >
                      <label>Number</label>
                      {renderEditableField(
                        'houseNumber',
                        'House Number',
                        property.houseNumber,
                        'text',
                      )}
                    </div>
                  </div>
                  <div className="detail-item-row">
                    <div
                      className="detail-item"
                      style={{ position: 'relative' }}
                    >
                      <label>City</label>
                      {renderEditableField(
                        'city',
                        'City',
                        property.city,
                        'text',
                      )}
                    </div>
                    <div
                      className="detail-item"
                      style={{ position: 'relative' }}
                    >
                      <label>Country</label>
                      {renderEditableField(
                        'country',
                        'Country',
                        property.country || 'Österreich',
                        'text',
                      )}
                    </div>
                  </div>

                  {/* Conditional fields based on property type */}
                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>GewType</label>
                      {editingField === 'gewType' ? (
                        renderEditableField(
                          'gewType',
                          'GewType',
                          property.gewType,
                          'select',
                          Object.values(GewType),
                        )
                      ) : (
                        <div
                          className="inline-edit-display"
                          onClick={() =>
                            handleFieldClick('gewType', property.gewType || '')
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleFieldClick(
                                'gewType',
                                property.gewType || '',
                              );
                            }
                          }}
                          tabIndex={0}
                          role="button"
                        >
                          {property.gewType ? (
                            String(property.gewType)
                          ) : (
                            <span className="placeholder">Select GewType</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Area (m²)</label>
                      {renderEditableField(
                        'area',
                        'Area',
                        property.area,
                        'number',
                      )}
                    </div>
                  </div>

                  <div className="detail-section-subtitle">
                    Ausstattung & Lage
                  </div>
                  <div className="attributes-grid">
                    {Object.entries(property.features || {}).map(
                      ([key, featObj]) => {
                        const label =
                          featObj &&
                          typeof featObj === 'object' &&
                          'localization' in featObj
                            ? featObj.localization
                            : key;
                        return (
                          <div className="detail-item" key={key}>
                            <label>{label}</label>
                            {renderEditableField(
                              `features.${key}`,
                              label,
                              featObj,
                              'checkbox',
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>

                  <div className="detail-section-subtitle">
                    Zustand & Qualität
                  </div>
                  <div
                    className="detail-item-row"
                    style={{
                      flexDirection: 'column',
                      gap: '1rem',
                      alignItems: 'stretch',
                    }}
                  >
                    {Object.entries(property.ratings || {}).map(
                      ([key, ratingObj]) => {
                        const label =
                          ratingObj &&
                          typeof ratingObj === 'object' &&
                          'localization' in ratingObj
                            ? ratingObj.localization
                            : key;
                        return (
                          <div
                            className="detail-item"
                            key={key}
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <label style={{ margin: 0 }}>{label}</label>
                            {renderRatingField(`ratings.${key}`)}
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="property-right-column">
              {/* Graph Section */}
              <div className="graph-section">
                <PropertyEvaluationChart
                  property={property}
                  refreshTrigger={0}
                />
              </div>

              <PropertyFiles propertyId={property.id} />

              {/* Financial Data Section */}
              <div className="detail-section">
                <h2>Financial Data</h2>
                <div className="detail-list">
                  <div className="detail-item">
                    <label>Purchase Price</label>
                    <div className="detail-value">
                      {editingField === 'kaufpreis' ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <span>€</span>
                          {renderEditableField(
                            'kaufpreis',
                            'Purchase Price',
                            property.kaufpreis,
                            'number',
                          )}
                        </div>
                      ) : (
                        <span
                          onClick={() =>
                            handleFieldClick(
                              'kaufpreis',
                              property.kaufpreis || 0,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleFieldClick(
                                'kaufpreis',
                                property.kaufpreis || 0,
                              );
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label="Edit Purchase Price"
                          className="inline-edit-display"
                          style={{ display: 'inline-block', cursor: 'pointer' }}
                          title="Click to edit or press Space or Enter"
                        >
                          {property.kaufpreis !== undefined ? (
                            `€${property.kaufpreis.toLocaleString()}`
                          ) : (
                            <span className="placeholder">
                              Click to add purchase price
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>{' '}
                  <div className="detail-item">
                    <label>Purchase Date</label>
                    <div className="detail-value">
                      {editingField === 'kaufdatum' ? (
                        renderEditableField(
                          'kaufdatum',
                          'Purchase Date',
                          property.kaufdatum,
                          'date',
                        )
                      ) : (
                        <span
                          onClick={() =>
                            handleFieldClick(
                              'kaufdatum',
                              property.kaufdatum || '',
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleFieldClick(
                                'kaufdatum',
                                property.kaufdatum || '',
                              );
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label="Edit Purchase Date"
                          className="inline-edit-display"
                          style={{ display: 'inline-block', cursor: 'pointer' }}
                          title="Click to edit or press Space or Enter"
                        >
                          {property.kaufdatum ? (
                            (() => {
                              const parts = property.kaufdatum.split('-');
                              return parts.length === 3
                                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                                : new Date(
                                    property.kaufdatum,
                                  ).toLocaleDateString();
                            })()
                          ) : (
                            <span className="placeholder">
                              Click to add purchase date
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Total Gain</label>
                    <div
                      className={`detail-value ${property.gain >= 0 ? 'stat-positive' : 'stat-negative'}`}
                    >
                      €{property.gain.toLocaleString()} (
                      {property.gainPercentage > 0 ? '+' : ''}
                      {property.gainPercentage.toFixed(1)}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Bottom Right */}
          <div className="action-buttons-container">
            <button
              className="save-button"
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPropertyPage;
